import _, { find } from 'lodash';
import pkceChallenge from 'pkce-challenge';
import * as React from 'react';
import { useCallback, useContext, useEffect, useState } from 'react';
import { I18nContext, I18nContextType, useI18n } from '../../common/i18n/I18n';
import Loading from '../components/Loading';
import { enqueueSnackbar } from '../components/Toast';
import {
    REFERRER_LOCAL_STORAGE_KEY,
    REFERRER_PREFIX,
    ROUTE_CONSTANTS,
    TP_DATA,
    TRANSFER_FLAG,
    USER_DATA
} from '../constants/constants';
import Validator, { ValidationState } from '../form/Validator';
import Nav from '../nav/Nav';
import useDefaultRoute from '../nav/useDefaultRoute';
import useHistory from '../utils/useHistory';
import { authLinks } from './AccountLinks';
import ChooseAccountForm from './ChooseAccountForm';
import CustomAuth from './CustomAuth';
import Login from './Login';
import PreLoginWithInviteInfoPanel from './PreLoginWithInviteInfoPanel';
import { UserContext } from './UserContext';
import UsernameLoginForm from './UsernameLoginForm';
import {
    Organization,
    PreLoginResponse,
    exchangeToken,
    fetchPreferredAccount,
    getEmailFromToken,
    preLogin,
    refreshToken,
    registerFlow,
    setToken
} from './api';
import {
    trackInitiatingSSORedirect,
    trackPKCEChallengeGenerated,
    trackSSOCallbackError,
    trackSSOCallbackSuccess,
    trackSSOCompletion
} from './authNetworkLogger';
import { BRANDING_NAME_QUERY_PARAM, INVITE_TYPE_QUERY_PARAM } from './constants';
import { useInviteCheck } from './hooks/useInviteCheck';
import {
    CODE_CHALLENGE,
    CODE_VERIFIER,
    CURRENT_IDP_IDENTIFIER,
    LINKED_USER_ERROR,
    NEW_USER_SIGNUP,
    USER_EXISTS_ERROR,
    buildSSORedirectURI
} from './ssoAuth';
import { extractBrandingNameFromReferrer, extractEmailFromReferrer, extractInviteTypeFromReferrer } from './utils';

const NEEDS_PASSWORD = 'NEEDS_PASSWORD';
const NEEDS_USERNAME = 'NEEDS_USERNAME';
const CHOOSE_ACCOUNT = 'CHOOSE_ACCOUNT';
const ACTIVE_ORG_ID = 'orgId';
const CUSTOM_AUTH = 'CUSTOM_AUTH';

const validationRules = {
    username: [Validator.RULES.isRequired, Validator.RULES.email]
};

const redirectToSSO = async (idpIdentifier: string, orgId: string, email: string) => {
    try {
        const { code_challenge, code_verifier } = await pkceChallenge();

        // Track PKCE generation
        trackPKCEChallengeGenerated(true);

        localStorage.setItem(CODE_CHALLENGE, code_challenge);
        localStorage.setItem(CODE_VERIFIER, code_verifier);
        localStorage.setItem(CURRENT_IDP_IDENTIFIER, idpIdentifier);
        localStorage.setItem(ACTIVE_ORG_ID, orgId);
        const uri = buildSSORedirectURI(idpIdentifier, code_challenge, email);

        // Track SSO redirect initiation
        trackInitiatingSSORedirect(idpIdentifier, orgId, email, uri);

        window.location.replace(uri);
    } catch (error) {
        console.error('Failed to initiate SSO redirect:', error);
        const errorMessage = error instanceof Error ? error.message : String(error);
        trackPKCEChallengeGenerated(false, errorMessage);
        enqueueSnackbar('Unable to initiate SSO login. Please try again or contact support.', { variant: 'error' });
    }
};

export default () => {
    const { changeRoute, searchParams, logOut, goBack, changeSearch, location } = useHistory();
    const { isAuthenticated, setIsAuthenticated, userProfile, setActiveOrganizationAccount } = useContext(UserContext);
    const [selectedOrgId, setSelectedOrgId] = useState<string>();
    const [loginState, setLoginState] = useState(NEEDS_USERNAME);
    const [busy, setBusy] = useState(false);
    const [orgAccounts, setOrgAccounts] = useState<Organization[]>([]);
    const i18nContext: I18nContextType = useContext(I18nContext);
    const { pathname, orgId } = useDefaultRoute(selectedOrgId);
    const [newToken, setNewToken] = useState('');
    const passwordSent = searchParams.get('tp');
    const [isBusy, setIsBusy] = useState(false);
    const fromInvite = location.state?.fromInvite || false;
    const inviteType =
        searchParams.get(INVITE_TYPE_QUERY_PARAM) || extractInviteTypeFromReferrer(location.state?.referrer) || '';
    const brandingName =
        searchParams.get(BRANDING_NAME_QUERY_PARAM) || extractBrandingNameFromReferrer(location.state?.referrer);

    const emailFromReferrer = location.state?.referrer && extractEmailFromReferrer(location.state.referrer);
    const [email, setEmail] = useState(location.state?.email ?? location.state?.username ?? emailFromReferrer ?? '');
    const [valid, setValid] = useState<ValidationState>({
        isValid: !!email,
        errors: {}
    });
    const { cachedPreLoginResponse, displayInviteInfoPanel, isCheckingInvite, checkInvite } =
        useInviteCheck(inviteType);

    // Default route will be available once user is logged in and user profile is loaded.
    useEffect(() => {
        if (userProfile) {
            // @todo Extract referrer consumption and precedence rules into a shared auth referrer utility.
            const ref =
                localStorage.getItem(REFERRER_LOCAL_STORAGE_KEY) ||
                localStorage.getItem(REFERRER_PREFIX + userProfile.username);
            if (ref) {
                let newRoute;
                try {
                    newRoute = JSON.parse(ref);
                } catch (error) {
                    console.warn('Failed to parse stored login referrer:', error);
                }

                localStorage.removeItem(REFERRER_PREFIX + userProfile.username);
                localStorage.removeItem(REFERRER_LOCAL_STORAGE_KEY);

                if (newRoute?.pathname) {
                    const referrerSearchParams = new URLSearchParams(newRoute.search);
                    const refOrgId = referrerSearchParams.get('orgId');
                    const hasAccess = !!refOrgId && !!find(userProfile.memberships, { id: refOrgId });
                    const fallbackOrgId = searchParams.get('orgId') || orgId;

                    if (!hasAccess && fallbackOrgId) {
                        referrerSearchParams.set('orgId', fallbackOrgId);
                    }

                    changeRoute(newRoute.pathname, referrerSearchParams, true);
                    return;
                }
            }

            if (pathname) {
                changeRoute(pathname, { orgId });
            } else {
                throw new Error('Default route not found, please check permissions.');
            }
        }
    }, [changeRoute, orgId, pathname, searchParams, userProfile]);

    useEffect(() => {
        if (inviteType && email && valid.isValid) {
            (async () => {
                setBusy(true);
                try {
                    await checkInvite(email);
                } catch (err) {
                    console.error('Invite check failed:', err);
                } finally {
                    setBusy(false);
                }
            })();
        }
    }, [inviteType, email, valid.isValid, checkInvite]);

    const validator = React.useMemo(
        () =>
            new Validator(validationRules, {
                // If true validator will keep going even after first field fails validation.
                runAll: false,
                i18nContext
            }),
        [i18nContext]
    );

    const extractTpFromReferrer = (referrer) => {
        try {
            const url = new URL(referrer, window.location.origin);
            const tp = url.searchParams.get(TP_DATA);

            return tp ? decodeURIComponent(tp) : '';
        } catch (error) {
            console.error('Invalid URL:', error);
            return '';
        }
    };

    const isPasswordSent = passwordSent === '1' || extractTpFromReferrer(location.state?.referrer) === '1';

    const handleOrganisationSelection = (res: PreLoginResponse) => {
        const ref = localStorage.getItem(REFERRER_PREFIX + email);
        const preferredAccount = fetchPreferredAccount(email);
        if (preferredAccount) setSelectedOrgId(preferredAccount);
        if (res.organizations && res.organizations.length > 1 && !ref && !preferredAccount) {
            setOrgAccounts(res.organizations);
            setLoginState(CHOOSE_ACCOUNT);
            setIsBusy(false);
        } else {
            if (res.organizations?.length) {
                const org = res.organizations[0];
                if (org.organizationDomain && org.organizationDomain.idpIdentifier) {
                    redirectToSSO(org.organizationDomain.idpIdentifier, org.id, email);
                } else {
                    setSelectedOrgId(org.id);
                    setLoginState(NEEDS_PASSWORD);
                    setIsBusy(false);
                }
            } else {
                setSelectedOrgId(res.accountId);
                setLoginState(NEEDS_PASSWORD);
                setIsBusy(false);
            }
        }
    };

    const processPreLoginResponse = useCallback(
        (res: PreLoginResponse, email: string) => {
            if (res.userFound) {
                if (res.action === 0) {
                    handleOrganisationSelection(res);
                    return;
                }
                if (res.action === 1) {
                    changeRoute(authLinks.verify.path, {}, true, { username: email });
                    return;
                }
                if (location.state?.email && res.action === 2 && fromInvite) {
                    registerFlow(email);
                    enqueueSnackbar('Email sent', { variant: 'success' });
                    handleOrganisationSelection(res);
                    return;
                }

                if (res.action === 2) {
                    const tokenData = localStorage.getItem('auth-token');
                    const hasToken = tokenData || newToken;
                    const shouldVerify = hasToken || isPasswordSent;

                    if (shouldVerify) {
                        changeRoute(authLinks.login.path, {}, true, { email });

                        if (hasToken) {
                            registerFlow(email);
                            enqueueSnackbar('Email sent', { variant: 'success' });
                        }

                        handleOrganisationSelection(res);
                        return;
                    }

                    setLoginState(CUSTOM_AUTH);
                    return;
                }
            } else if (email) {
                changeRoute(authLinks.signup.path, {}, true, { username: email });
                setValid({ errors: validator.errors, isValid: _.isEmpty(validator.errors) });
            }
        },
        [handleOrganisationSelection, changeRoute, location.state, fromInvite, newToken, isPasswordSent, validator]
    );

    const onContinue = React.useCallback(
        async (email) => {
            const rawUserData = localStorage.getItem(USER_DATA);
            let parsed;
            if (rawUserData) {
                try {
                    parsed = JSON.parse(rawUserData);
                } catch (e) {
                    console.error('Failed to parse USER_DATA', e);
                }
            }
            if (!parsed?.professionalRole) {
                localStorage.removeItem(USER_DATA);
            } else {
                localStorage.removeItem(TRANSFER_FLAG);
            }

            if (cachedPreLoginResponse && inviteType) {
                processPreLoginResponse(cachedPreLoginResponse, email);
            } else {
                setIsBusy(true);
                try {
                    const res = await preLogin(email);
                    processPreLoginResponse(res, email);
                } catch (err) {
                    enqueueSnackbar(`Unable to proceed with Login: ${err.message}`, { variant: 'error' });
                } finally {
                    setIsBusy(false);
                }
            }
        },
        [cachedPreLoginResponse, inviteType, processPreLoginResponse]
    );

    const handleSelectAccount = (orgAcc: Organization) => {
        const idpIdentifier = orgAcc?.organizationDomain?.idpIdentifier;
        if (idpIdentifier) {
            redirectToSSO(idpIdentifier, orgAcc.id, email);
        } else {
            setLoginState(NEEDS_PASSWORD);
            setSelectedOrgId(orgAcc.id);
        }
    };

    const cancel = () => {
        setLoginState(NEEDS_USERNAME);
        setOrgAccounts([]);
        goBack();
    };

    const hasErrors = React.useCallback(
        (fieldName) => {
            return Array.isArray(valid.errors[fieldName]) && valid.errors[fieldName].length > 0;
        },
        [valid.errors]
    );

    let formErrors = [];
    if (valid.errors.length) {
        Object.keys(valid.errors).map((key) => {
            if (Array.isArray(valid.errors[key])) {
                formErrors = formErrors.concat(valid.errors[key]);
            }
        });
        console.warn('Form errors:', formErrors);
    }

    const keyboardEventHandler = React.useCallback(
        (event, email) => {
            if (event.key === 'Enter' && valid.isValid) {
                onContinue(email);
            }
        },
        [valid.isValid, onContinue]
    );

    useEffect(() => {
        // for first time linked user we need to redirect again
        if (searchParams && searchParams.get('error_description')) {
            setBusy(true);
            const description = searchParams.get('error_description');
            // Track SSO callback error
            trackSSOCallbackError(description);
            if (description.includes(LINKED_USER_ERROR) || description.includes(USER_EXISTS_ERROR)) {
                const idpIdentifier = localStorage.getItem(CURRENT_IDP_IDENTIFIER);
                const codeChallenge = localStorage.getItem(CODE_CHALLENGE);
                if (idpIdentifier && codeChallenge) {
                    const uri = buildSSORedirectURI(idpIdentifier, codeChallenge, '');
                    window.location.replace(uri);
                }
            } else if (description.includes(NEW_USER_SIGNUP)) {
                handleSocialSigninError(description);
            } else {
                setBusy(false);
            }
        }
    }, []);

    useEffect(() => {
        if (searchParams && searchParams.get('code')) {
            const code = searchParams.get('code');
            const verifier = localStorage.getItem(CODE_VERIFIER);
            const orgId = localStorage.getItem(ACTIVE_ORG_ID);
            // Track SSO callback received
            trackSSOCallbackSuccess(code);
            setBusy(true);
            exchangeToken({ code, code_verifier: verifier })
                .then((data) => {
                    refreshToken(data['refresh_token'])
                        .then((data) => {
                            changeSearch({ code: undefined });
                            handleSuccessfulLogin(data['idToken'], orgId);
                            // Track SSO completion
                            trackSSOCompletion(true, orgId);
                        })
                        .catch((err) => {
                            trackSSOCompletion(false, orgId, err?.message || 'Token refresh failed');
                            setBusy(false);
                            logOut();
                        });
                })
                .catch((err) => {
                    setBusy(false);
                    logOut();
                })
                .finally(() => {
                    localStorage.removeItem(CODE_CHALLENGE);
                    localStorage.removeItem(CODE_VERIFIER);
                    localStorage.removeItem(CURRENT_IDP_IDENTIFIER);
                    localStorage.removeItem(ACTIVE_ORG_ID);
                });
        }
    }, []);

    const handleSuccessfulLogin = (idToken: string, orgId: string) => {
        setToken(idToken);
        if (!orgId) {
            const email = getEmailFromToken(idToken);
            const preferredAccount = fetchPreferredAccount(email);
            if (preferredAccount) orgId = preferredAccount;
        }
        if (orgId) {
            localStorage.setItem(ROUTE_CONSTANTS.lastSelectedOrgId, orgId);
        }
        setActiveOrganizationAccount(orgId).then(() => {
            changeSearch({ orgId: orgId });
            setIsAuthenticated(true);
        });
    };

    const handleSocialSigninError = (error: string) => {
        var decodedResponse = decodeURIComponent(error);
        const sIdx = decodedResponse.indexOf('{');
        const eIdx = decodedResponse.lastIndexOf('}') + 1;
        const json = decodedResponse.substring(sIdx, eIdx);
        interface signupRequest {
            fName: string;
            lName: string;
            email: string;
        }
        let obj: signupRequest = JSON.parse(json);
        changeRoute(authLinks.signup.path, {}, true, {
            username: obj.email,
            firstName: obj.fName,
            lastName: obj.lName,
            socialSignUp: true
        });
    };

    const title = useI18n('dialog.login');
    const chooseAccountTitle = useI18n('dialog.choose.account');

    const loginContent = (
        <React.Fragment>
            {loginState === NEEDS_PASSWORD && (
                <Login
                    username={email}
                    setLoginState={setLoginState}
                    newToken={newToken}
                    onLogin={(token: string) => {
                        handleSuccessfulLogin(token, selectedOrgId);
                    }}
                    cancel={cancel}
                />
            )}
            {loginState === CUSTOM_AUTH && (
                <CustomAuth
                    username={email}
                    setBearer={(token) => {
                        if (token) {
                            setToken(token);
                            setNewToken(token);
                            onContinue(email);
                        }
                    }}
                />
            )}

            {loginState === NEEDS_USERNAME && (
                <UsernameLoginForm
                    keyboardEventHandler={keyboardEventHandler}
                    title={title}
                    email={email}
                    setEmail={setEmail}
                    hasErrors={hasErrors}
                    isBusy={isBusy}
                    onContinue={onContinue}
                    valid={valid}
                    setValid={setValid}
                    validator={validator}
                    emailFromReferrer={emailFromReferrer}
                    backgroundColor={displayInviteInfoPanel ? 'inviteInfo.backgroundColor' : undefined}
                />
            )}
            {loginState === CHOOSE_ACCOUNT && (
                <ChooseAccountForm
                    chooseAccountTitle={chooseAccountTitle}
                    email={email}
                    setEmail={setEmail}
                    valid={valid}
                    hasErrors={hasErrors}
                    orgAccounts={orgAccounts}
                    handleSelectAccount={handleSelectAccount}
                    cancel={cancel}
                />
            )}
        </React.Fragment>
    );

    return isAuthenticated && userProfile ? (
        <React.Fragment />
    ) : (
        <>
            <Nav />
            {isCheckingInvite || busy ? (
                <Loading />
            ) : displayInviteInfoPanel && loginState === NEEDS_USERNAME && !isCheckingInvite && !busy ? (
                <PreLoginWithInviteInfoPanel brandingName={brandingName} inviteType={inviteType}>
                    {loginContent}
                </PreLoginWithInviteInfoPanel>
            ) : (
                !isCheckingInvite && !busy && loginContent
            )}
        </>
    );
};
